import React, { useState, useEffect } from 'react';
import { Box, Button } from 'rebass';
import { useToasts } from 'react-toast-notifications';
import { Input, Label, Radio } from '@rebass/forms';

import NiceModal from 'components/NiceModal';
import CreatePanel from 'components/Participant/CreatePanel';
import PanelSelect from 'components/PanelSelect';
import services from 'services/services';
import service_people from 'services/people';
import { useAuth } from 'context/AuthContext';
import { useCreatePanelistMutation } from 'api/useCreatePanelistMutation';
import InformationTooltip from 'components/InformationTooltip';

export default function CreateParticipant(props) {
    const { addToast } = useToasts();
    const auth = useAuth();
    const [panelIdSelected, setPanelIdSelected] = useState(null);
    const [showModalCreatePanel, setShowModalCreatePanel] = useState(false);
    const { mutate: createPerson, isLoading: isPersonCreating } = useCreatePanelistMutation({
        onSuccess: data => {
            props.onClose();
            props.onCreate(data);
        },
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error);
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: true
            });
        }
    });

    const isDoubleOptInEnabled = !!auth?.account?.settings?.is_double_opt_in_enabled;

    function getPanelsXHR(selectId) {
        service_people
            .getPanels()
            .then(panels_response => {
                props.onUpdatePanels(panels_response);
                if (selectId) {
                    setPanelIdSelected(selectId);
                }
            })
            .catch(error => {
                const errorText = services.parseAndTrackXhrErrors(error);
                addToast(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    function onCreatePanel(panel) {
        addToast('Successfully created a new panel', {
            appearance: 'success',
            autoDismiss: true
        });
        getPanelsXHR(panel.id);
    }

    function onSubmit(e) {
        e.preventDefault();

        const data = new FormData(e.target);

        /**
         * @type {Object}
         * @property {string} firstName
         * @property {string} lastName
         * @property {string} email
         * @property {number} panelId
         * @property {number} [skipDoubleOptIn]
         */
        const person = {
            firstName: data.get('create_person_first_name'),
            lastName: data.get('create_person_last_name'),
            email: data.get('create_person_email'),
            panelId: panelIdSelected
        };

        // Check if double opt-in is enabled
        if (isDoubleOptInEnabled) {
            person.skipDoubleOptIn = +data.get('skip-double-opt-in');
        }

        createPerson(person);
    }

    useEffect(() => {
        if (props.defaultPanelIdSelected) {
            setPanelIdSelected(props.defaultPanelIdSelected);
        } else if (props.panels && props.panels.length) {
            setPanelIdSelected(props.panels[0].id);
        }
    }, [props.defaultPanelIdSelected, props.panels]);

    return (
        <>
            <form onSubmit={onSubmit}>
                <div>
                    <Box mb={3}>
                        <Label>Panel</Label>
                        <PanelSelect
                            width="100%"
                            items={props.panels}
                            value={panelIdSelected}
                            onChange={panel_id => setPanelIdSelected(panel_id)}
                            onCreatePanel={() => setShowModalCreatePanel(true)}
                        />
                    </Box>
                    <Label>First Name</Label>
                    <Input type="text" name="create_person_first_name" placeholder="John" autoComplete="off" required />
                    <Label mt={3}>Last Name</Label>
                    <Input type="text" name="create_person_last_name" placeholder="Smith" autoComplete="off" required />
                    <Label mt={3}>Email</Label>
                    <Input
                        type="text"
                        name="create_person_email"
                        placeholder="jsmith@gmail.com"
                        autoComplete="off"
                        required
                    />
                </div>
                {isDoubleOptInEnabled && (
                    <fieldset className="border-0 p-0 m-0 mt-8" aria-describedby="skip-double-opt-in-description">
                        <legend className="p-0 fs-accent-14 mb-1">Double Opt-in</legend>
                        <p id="skip-double-opt-in-description" className="fs-body-12 color-text-secondary m-0 mb-5">
                            Your organization requires email confirmation before panelists are marked as opted in.
                            Choose how you want this panelist confirmed.
                        </p>
                        <Label htmlFor="skip-double-opt-in-yes" className="!mb-0 h-8 mb-4">
                            <Radio
                                id="skip-double-opt-in-yes"
                                name="skip-double-opt-in"
                                value="0"
                                defaultChecked
                                className="!mr-1 !my-1.5"
                            />
                            <span className="leading-8">Use Double Opt-in (recommended)</span>
                            <InformationTooltip
                                id="skip-double-opt-in-yes-tooltip"
                                place="top"
                                className="!m-0 !my-2 !ml-1"
                            >
                                We’ll email this panelist a confirmation link. After they click it, they’ll be marked as
                                double opted in.
                            </InformationTooltip>
                        </Label>
                        <Label htmlFor="skip-double-opt-in-no" className="!mb-0 h-8">
                            <Radio
                                id="skip-double-opt-in-no"
                                name="skip-double-opt-in"
                                value="1"
                                className="!mr-1 !my-1.5"
                            />
                            <span className="leading-8">Skip Email and Mark as Double Opt-In</span>
                            <InformationTooltip
                                id="skip-double-opt-in-no-tooltip"
                                place="top"
                                className="!m-0 !my-2 !ml-1"
                            >
                                Don’t send a confirmation email. Use only if you’ve already confirmed consent outside of
                                dscout.
                            </InformationTooltip>
                        </Label>
                    </fieldset>
                )}
                <div className="modal-actions">
                    <Button variant="secondary-gray" type="button" mr={3} onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        mr={0}
                        className="modal-primary"
                        disabled={isPersonCreating}
                    >
                        Create panelist
                    </Button>
                </div>
            </form>
            <NiceModal
                isOpen={showModalCreatePanel}
                shouldCloseOnOverlayClick
                onRequestClose={() => setShowModalCreatePanel(false)}
                title={`Create new panel`}
            >
                <CreatePanel onCreate={onCreatePanel} onClose={() => setShowModalCreatePanel(false)} />
            </NiceModal>
        </>
    );
}
